import * as React from 'react';
import { AlertTriangle, Info, OctagonAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

export type BannerTone = 'danger' | 'warning' | 'info';

const TONES: Record<BannerTone, { box: string; icon: React.ComponentType<{ className?: string }> }> = {
  danger: {
    box: 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/60 dark:text-rose-200',
    icon: OctagonAlert,
  },
  warning: {
    box: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
    icon: AlertTriangle,
  },
  info: {
    box: 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/60 dark:text-sky-200',
    icon: Info,
  },
};

export interface BannerProps {
  tone: BannerTone;
  title: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

/** Full-width notice used for the on-site rule, restrictions and portal state on the detail page. */
export function Banner({ tone, title, children, className }: BannerProps) {
  const { box, icon: Icon } = TONES[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3 text-sm', box, className)}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">{title}</p>
        {children ? <div className="whitespace-pre-wrap opacity-90">{children}</div> : null}
      </div>
    </div>
  );
}
